import { Component, Input, OnChanges, OnInit, SimpleChanges, isDevMode } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { environment } from '../../../../../environments/environment';
import {endOfDay} from '../../../../utils/date-range-format.service';

@Component({
  selector: 'app-counselling-assessment-charts',
  templateUrl: './counselling-assessment-charts.component.html',
  styleUrls: ['./counselling-assessment-charts.component.scss']
})
export class CounsellingAssessmentChartsComponent implements OnInit, OnChanges {

  @Input() selectedCentreCodes: string[] = [];
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() isNestedComponent: boolean = false;

  private env = isDevMode() ? environment.testMode : environment.productionMode;

  // Chart data
  showAdmissionChart = false;
  showDischargeChart = false;

  admissionChartData: number[] = [];
  dischargeChartData: number[] = [];

  doughnutChartLabels: string[] = ['😠 Difficulty in coping', '😊 Able to Cope up', '😐 Sometimes'];

  isLoading = false;

  // Counts
  admissionCount = 0;
  dischargeCount = 0;

  constructor(private firestore: AngularFirestore) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startDate'] || changes['endDate'] || changes['selectedCentreCodes']) {
      if (this.startDate && this.endDate) {
        this.fetchAssessments();
      }
    }
  }

  private fetchAssessments(): void {
    this.isLoading = true;
    this.showAdmissionChart = false;
    this.showDischargeChart = false;
    this.admissionChartData = [];
    this.dischargeChartData = [];

    const start = new Date(this.startDate!);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.endDate!);
    end.setHours(23, 59, 59, 999);

    const assessmentPath = `${this.env.assessment}/allAssessments/assessments`;

    this.firestore.collection<any>(assessmentPath, ref =>
      ref.where('dateOfAssessment', '>=', start)
         .where('dateOfAssessment', '<=', endOfDay(end))
         .where('type', 'in', ['Admission Baseline Assessment', 'Discharge Assessment'])
    ).get().subscribe({
      next: (snapshot) => {
        let assessments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filter by selected centres if not "All Centres"
        const isAllCentres = this.selectedCentreCodes.includes('All Centres') || this.selectedCentreCodes.length === 0;
        if (!isAllCentres) {
          assessments = assessments.filter(a => this.selectedCentreCodes.includes(a.centreCode));
        }

        // Separate into admission and discharge
        const admissionAssessments = assessments.filter((a: any) => a.type === 'Admission Baseline Assessment');
        const dischargeAssessments = assessments.filter((a: any) => a.type === 'Discharge Assessment');

        this.admissionCount = admissionAssessments.length;
        this.dischargeCount = dischargeAssessments.length;

        // Build admission chart
        if (admissionAssessments.length > 0) {
          this.buildChart(admissionAssessments, 'admission');
        }

        // Build discharge chart
        if (dischargeAssessments.length > 0) {
          this.buildChart(dischargeAssessments, 'discharge');
        }

        this.isLoading = false;

        console.log('📊 Assessment Charts:', {
          totalAssessments: assessments.length,
          admission: admissionAssessments.length,
          discharge: dischargeAssessments.length
        });
      },
      error: (err) => {
        console.error('❌ Error fetching assessments for charts:', err);
        this.isLoading = false;
      }
    });
  }

  private buildChart(assessments: any[], type: 'admission' | 'discharge'): void {
    let totalYes = 0;
    let totalNo = 0;
    let totalSometimes = 0;

    assessments.forEach((it: any) => {
      if (it.assessment && Array.isArray(it.assessment)) {
        totalYes += it.assessment.filter((x: any) => x.ans === 'Yes').length;
        totalNo += it.assessment.filter((x: any) => x.ans === 'No').length;
        totalSometimes += it.assessment.filter((x: any) => x.ans === 'Sometimes').length;
      }
    });

    const total = totalYes + totalNo + totalSometimes;
    if (total === 0) return;

    const yesPercentage = Number(((totalYes / total) * 100).toFixed(2));
    const noPercentage = Number(((totalNo / total) * 100).toFixed(2));
    const sometimesPercentage = Number(((totalSometimes / total) * 100).toFixed(2));

    // Chart data order: [Yes (Difficulty in coping), No (Able to Cope up), Sometimes]
    const chartData = [yesPercentage, noPercentage, sometimesPercentage];

    if (type === 'admission') {
      this.admissionChartData = chartData;
      this.showAdmissionChart = true;
    } else {
      this.dischargeChartData = chartData;
      this.showDischargeChart = true;
    }
  }
}
